import { useEffect, useState, type ReactNode } from "react";
import { COMPANY } from "../lib/company";

export type TocEntry = { id: string; title: string };

/**
 * Highlights the section currently under the reader.
 *
 * Returns "" on the server and on the first client paint, so the prerendered
 * markup and the hydrated markup agree — the highlight appears one frame later.
 */
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState("");

  useEffect(() => {
    if (ids.length === 0 || typeof IntersectionObserver === "undefined") return;

    const seen = new Map<string, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) seen.set(e.target.id, e.isIntersecting);
        // The topmost visible section wins, so scrolling up highlights the
        // section you are entering rather than the one you are leaving.
        const first = ids.find((id) => seen.get(id));
        if (first) setActive(first);
      },
      // Ignore the band under the sticky header and the bottom two thirds, so a
      // single section is in view at a time on tall screens.
      { rootMargin: "-72px 0px -66% 0px" },
    );

    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids.join("|")]);

  return active;
}

/** Shared chrome for Privacy Policy, Terms of Service, Data Deletion and Security. */
export function LegalPage({
  eyebrow = `${COMPANY.product} legal`,
  title,
  intro,
  toc,
  /** Security is a standing statement rather than a dated instrument, so it omits the dates. */
  showDates = true,
  children,
}: {
  eyebrow?: string;
  title: string;
  intro: ReactNode;
  toc?: TocEntry[];
  showDates?: boolean;
  children: ReactNode;
}) {
  const active = useActiveSection(toc?.map((t) => t.id) ?? []);
  const hasToc = !!toc && toc.length > 0;

  return (
    <article className={hasToc ? "legal legal--toc" : "legal"}>
      <div className="legal-head">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="lede">{intro}</p>
        {showDates && (
          <p className="muted">
            Effective Date: {COMPANY.effectiveDate} &nbsp;·&nbsp; Last Updated: {COMPANY.lastUpdated}
          </p>
        )}
      </div>

      {hasToc && (
        <aside className="legal-aside">
          <nav className="legal-toc" aria-label="Sections">
            <h2>Sections</h2>
            <ol>
              {toc.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className={t.id === active ? "is-active" : undefined}
                    aria-current={t.id === active ? "true" : undefined}
                  >
                    {t.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </aside>
      )}

      <div className="legal-body">{children}</div>
    </article>
  );
}

/** One numbered legal section, anchored so the table of contents can link to it. */
export function Sec({ id, n, title, children }: {
  id: string; n: number; title: string; children: ReactNode;
}) {
  return (
    <section id={id} className="legal-sec">
      <h2>{n}. {title}</h2>
      {children}
    </section>
  );
}
